import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Table, TableColumn } from '@/components/common';
import { MaterialStock } from '@/types/material';
import { formatQuantity } from '@/utils/number-format';

interface MaterialStockTabProps {
  materialCode: string;
  stockRecords: MaterialStock[];
}

const MaterialStockTab: React.FC<MaterialStockTabProps> = ({
  materialCode: _materialCode,
  stockRecords,
}) => {
  const { t } = useTranslation();

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      in_use: { color: 'bg-blue-100 text-blue-800', label: t('materials.stockStatus.in_use') },
      available: { color: 'bg-green-100 text-green-800', label: t('materials.stockStatus.available') },
      reserved: { color: 'bg-yellow-100 text-yellow-800', label: t('materials.stockStatus.reserved') },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.available;
    return (
      <span className={classNames('px-2 py-1 text-xs font-medium rounded-full', config.color)}>
        {config.label}
      </span>
    );
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const columns: TableColumn<MaterialStock>[] = [
    {
      key: 'productName',
      title: 'Tên sản phẩm',
      dataIndex: 'productName',
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      ),
    },
    {
      key: 'unit',
      title: 'Đơn vị',
      dataIndex: 'unit',
      width: 80,
      align: 'center',
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value}
        </div>
      ),
    },
    {
      key: 'quantityUsed',
      title: 'Số lượng đã dùng',
      dataIndex: 'quantityUsed',
      width: 140,
      align: 'right',
      render: (value, record) => (
        <div className="text-sm font-medium text-red-600">
          {formatQuantity(value)} {record.unit}
        </div>
      ),
    },
    {
      key: 'quantityInStock',
      title: 'Số lượng tồn kho',
      dataIndex: 'quantityInStock',
      width: 140,
      align: 'right',
      render: (value, record) => (
        <div className="text-sm font-medium text-green-600">
          {formatQuantity(value)} {record.unit}
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 130,
      render: (value) => getStatusBadge(value),
    },
    {
      key: 'lastUpdated',
      title: 'Cập nhật cuối',
      dataIndex: 'lastUpdated',
      width: 150,
      render: (value) => (
        <div className="text-sm text-gray-600">
          {formatDateTime(value)}
        </div>
      ),
    },
  ];

  // Calculate totals
  const totalUsed = stockRecords.reduce((sum, record) => sum + record.quantityUsed, 0);
  const totalInStock = stockRecords.reduce((sum, record) => sum + record.quantityInStock, 0);
  const totalAllocated = totalUsed + totalInStock;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-blue-600 font-medium">Tổng phân bổ</div>
          <div className="text-2xl font-bold text-blue-900">{totalAllocated}</div>
          <div className="text-xs text-blue-600">Đã phân bổ cho sản phẩm</div>
        </div>
        
        <div className="bg-red-50 rounded-lg p-4">
          <div className="text-sm text-red-600 font-medium">Đã sử dụng</div>
          <div className="text-2xl font-bold text-red-900">{totalUsed}</div>
          <div className="text-xs text-red-600">{t('materials.stockTab.usedInProduction')}</div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm text-green-600 font-medium">Còn lại</div>
          <div className="text-2xl font-bold text-green-900">{formatQuantity(totalInStock)}</div>
          <div className="text-xs text-green-600">Còn trong kho</div>
        </div>
      </div>

      {/* Stock Table - center of modal body as specified */}
      <div className="border border-gray-200 rounded-lg">
        <Table
          columns={columns}
          data={stockRecords}
          rowKey="id"
          size="sm"
          hoverable
          emptyText={t('materials.stockTab.emptyStateTitle')}

        />
      </div>

      {/* Usage Analysis or Empty State */}
      {stockRecords.length > 0 ? (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Phân tích sử dụng</h4>
          <div className="space-y-2">
            {stockRecords.map((record) => {
              const usagePercentage = record.quantityUsed / (record.quantityUsed + record.quantityInStock) * 100;
              return (
                <div key={record.id} className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 flex-1">
                    {record.productName}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${usagePercentage}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-600 w-12 text-right">
                      {usagePercentage.toFixed(0)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('materials.stockTab.emptyStateTitle')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('materials.stockTab.emptyStateDescription')}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {t('materials.stockTab.emptyStateNote')}
          </p>
        </div>
      )}

      {/* Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div className="text-center">
          <div className="text-gray-600">{t('materials.stockTab.summaryInUse')}</div>
          <div className="font-medium text-blue-600">
            {stockRecords.filter(r => r.status === 'in_use').length} {t('materials.stockTab.products')}
          </div>
        </div>
        <div className="text-center">
          <div className="text-gray-600">{t('materials.stockTab.summaryAvailable')}</div>
          <div className="font-medium text-green-600">
            {stockRecords.filter(r => r.status === 'available').length} {t('materials.stockTab.products')}
          </div>
        </div>
        <div className="text-center">
          <div className="text-gray-600">{t('materials.stockTab.summaryReserved')}</div>
          <div className="font-medium text-yellow-600">
            {stockRecords.filter(r => r.status === 'reserved').length} {t('materials.stockTab.products')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialStockTab;
